"use client";

import { useEffect } from "react";

import { useCallbackRef } from "../useCallbackRef/useCalbackRef";

export type Breakpoint = "sm" | "md" | "lg" | "xl" | "2xl";

type BreakpointValues = Record<Breakpoint, number>;

// Breakpoints from Tailwind CSS (https://tailwindcss.com/docs/responsive-design)
export const breakpoints: BreakpointValues = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
};

/**
 * Primitive hook that fires a callback whenever a breakpoint is matched or unmatched.
 * The listener is invoked immediately on mount with the current match state, then again
 * whenever the match state changes.
 *
 * Use this hook when you need to react imperatively to breakpoint changes (e.g. triggering
 * side effects, updating external state). For declarative boolean state, use `useBreakpoint` instead.
 *
 * @param breakpoint The breakpoint to observe ('sm', 'md', 'lg', 'xl', '2xl')
 * @param listener Callback invoked whenever the breakpoint boundary is crossed.
 *                 Receives `true` when the viewport width is at or above the breakpoint (min-width met),
 *                 and `false` when it falls below. Inline functions are safe — the ref is kept stable internally.
 */
export const useBreakpointListener = (breakpoint: Breakpoint, listener: (isAtOrAbove: boolean) => void): void => {
  const listenerRef = useCallbackRef(listener);

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(min-width: ${breakpoints[breakpoint]}px)`);

    const check = (matches: boolean) => {
      listenerRef.current(matches);
    };

    // Fire immediately so the caller has the current state on mount.
    check(mediaQuery.matches);

    const handler = (event: MediaQueryListEvent) => {
      check(event.matches);
    };

    mediaQuery.addEventListener("change", handler);

    return () => mediaQuery.removeEventListener("change", handler);
  }, [breakpoint, listenerRef]);
};
